import { core } from './client';
import type {
  ApproverType,
  ConditionOp,
  WorkflowCondition,
  WorkflowDecision,
  WorkflowDefinition,
  WorkflowDefinitionStatus,
  WorkflowHistoryEvent,
  WorkflowInstance,
  WorkflowInstanceStatus,
  WorkflowProcess,
  WorkflowRules,
  WorkflowStep,
  WorkflowValidationReport,
} from '@/types/workflows';

/**
 * The workflow console's calls, in one file — src/api/studio.ts's discipline
 * applied to the maker-checker service.
 *
 * ROUTING. Two controllers on campusone-core-service serve this page and they are
 * scoped differently on purpose:
 *
 *  - `/admin/institution/:institutionId/workflows/...` — the governance side.
 *    Only an institution administrator can read the catalogue or move a
 *    definition through its lifecycle.
 *  - `/institutions/:institutionId/workflows/...` — the member side. ANY member
 *    can hold an inbox and decide the steps that name them; putting these behind
 *    the admin guard would 403 every Principal who is an approver but not an
 *    administrator.
 *
 * VALIDATED AT THE BOUNDARY. Every body is rebuilt field by field before React
 * sees it. A definition whose steps are not an array would otherwise reach the
 * chain layout and blank the designer three renders later with a stack that
 * names a component instead of an endpoint. Junk LIST entries are dropped one by
 * one — eleven readable versions and one broken one render as eleven versions —
 * while a single unreadable OBJECT (one definition, one instance) throws here,
 * because "the server answered garbage" and "there is nothing configured" are
 * different statements and only one of them is actionable.
 */

const seg = encodeURIComponent;

const adminBase = (institutionId: string) => `/admin/institution/${seg(institutionId)}/workflows`;
const memberBase = (institutionId: string) => `/institutions/${seg(institutionId)}/workflows`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function fail(what: string): never {
  throw new Error(`The server returned ${what} this app cannot read.`);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

const APPROVER_TYPES: readonly ApproverType[] = ['ROLE', 'POSITION', 'ANY_OF_ROLES'];
const CONDITION_OPS: readonly ConditionOp[] = ['GT', 'GTE', 'LT', 'EQ', 'IN'];
const DEFINITION_STATUSES: readonly WorkflowDefinitionStatus[] = ['DRAFT', 'ACTIVE', 'SUPERSEDED'];
const INSTANCE_STATUSES: readonly WorkflowInstanceStatus[] = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
];

/**
 * One step. Tolerant on decoration (a missing title renders as an empty card,
 * not a crash) but strict on the fields the chain's semantics hang on: an entry
 * that is not even a record is unrepresentable and gets dropped by the caller.
 */
function step(value: unknown, index: number): WorkflowStep | null {
  if (!isRecord(value)) return null;
  const approverType = APPROVER_TYPES.includes(value.approverType as ApproverType)
    ? (value.approverType as ApproverType)
    : 'ROLE';
  const approverRefs = stringArray(value.approverRefs);
  const quorum =
    typeof value.quorum === 'number' && Number.isFinite(value.quorum) && value.quorum >= 1
      ? Math.floor(value.quorum)
      : 1;
  return {
    stepNo: typeof value.stepNo === 'number' && value.stepNo >= 1 ? Math.floor(value.stepNo) : index + 1,
    title: typeof value.title === 'string' ? value.title : '',
    approverType,
    approverRefs,
    quorum,
    allowSelfApproval: value.allowSelfApproval === true,
    timeoutHours:
      typeof value.timeoutHours === 'number' && value.timeoutHours > 0 ? value.timeoutHours : null,
    escalateTo: optionalString(value.escalateTo),
  };
}

function steps(value: unknown): WorkflowStep[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => step(entry, index))
    .filter((entry): entry is WorkflowStep => entry !== null)
    .sort((a, b) => a.stepNo - b.stepNo);
}

function condition(value: unknown): WorkflowCondition | null {
  if (!isRecord(value)) return null;
  if (typeof value.field !== 'string' || value.field.length === 0) return null;
  if (!CONDITION_OPS.includes(value.op as ConditionOp)) return null;
  const raw = value.value;
  const parsed =
    typeof raw === 'string' || typeof raw === 'number' ? raw : Array.isArray(raw) ? stringArray(raw) : null;
  if (parsed === null) return null;
  return { field: value.field, op: value.op as ConditionOp, value: parsed };
}

/**
 * Rules default SAFE, not permissive: an absent initiatorCannotApprove reads as
 * true, because rendering the maker-checker guarantee as "off" when the server
 * simply omitted the field would tell an administrator their control is weaker
 * than it is — the direction of error that erodes trust in the whole screen.
 */
function rules(value: unknown): WorkflowRules {
  const record = isRecord(value) ? value : {};
  return {
    initiatorCannotApprove: record.initiatorCannotApprove !== false,
    distinctApproversAcrossSteps: record.distinctApproversAcrossSteps === true,
    condition: condition(record.condition),
  };
}

function definition(payload: unknown): WorkflowDefinition {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.processKey !== 'string' ||
    !DEFINITION_STATUSES.includes(payload.status as WorkflowDefinitionStatus)
  ) {
    fail('a workflow definition');
  }
  return {
    id: payload.id,
    institutionId: optionalString(payload.institutionId),
    processKey: payload.processKey,
    title: typeof payload.title === 'string' ? payload.title : payload.processKey,
    description: typeof payload.description === 'string' ? payload.description : '',
    version: typeof payload.version === 'number' ? payload.version : 0,
    status: payload.status as WorkflowDefinitionStatus,
    steps: steps(payload.steps),
    rules: rules(payload.rules),
    createdBy: optionalString(payload.createdBy),
    createdAt: optionalString(payload.createdAt),
    approvedBy: optionalString(payload.approvedBy),
  };
}

function definitionList(payload: unknown): WorkflowDefinition[] {
  if (!Array.isArray(payload)) fail('a workflow definition list');
  return payload.flatMap((entry) => {
    try {
      return [definition(entry)];
    } catch {
      return [];
    }
  });
}

function process(value: unknown): WorkflowProcess | null {
  if (!isRecord(value) || typeof value.key !== 'string') return null;
  const active = isRecord(value.activeVersion) && typeof value.activeVersion.id === 'string'
    ? {
        id: value.activeVersion.id,
        version: typeof value.activeVersion.version === 'number' ? value.activeVersion.version : 0,
        stepsSummary: optionalString(value.activeVersion.stepsSummary),
      }
    : null;
  return {
    key: value.key,
    title: typeof value.title === 'string' && value.title ? value.title : value.key,
    description: typeof value.description === 'string' ? value.description : '',
    subjectType: typeof value.subjectType === 'string' ? value.subjectType : '',
    ownerModule: typeof value.ownerModule === 'string' && value.ownerModule ? value.ownerModule : 'GENERAL',
    hasInstitutionDefinition: value.hasInstitutionDefinition === true,
    activeVersion: active,
    platformDefault: isRecord(value.platformDefault)
      ? { version: typeof value.platformDefault.version === 'number' ? value.platformDefault.version : 0 }
      : null,
  };
}

function processList(payload: unknown): WorkflowProcess[] {
  if (!Array.isArray(payload)) fail('a workflow process catalogue');
  return payload
    .map(process)
    .filter((entry): entry is WorkflowProcess => entry !== null);
}

/**
 * The server's validation verdict, shown VERBATIM on the designer — the client
 * mirror is a courtesy, this is the ruling. `valid` is only trusted when it is
 * literally true, so a half-formed report reads as "not valid" rather than as a
 * green light nobody actually gave.
 */
function validationReport(payload: unknown): WorkflowValidationReport {
  if (!isRecord(payload)) fail('a validation report');
  const errors = Array.isArray(payload.errors)
    ? payload.errors.flatMap((entry) => {
        if (typeof entry === 'string') return [{ code: null, message: entry }];
        if (!isRecord(entry)) return [];
        const message = optionalString(entry.message) ?? optionalString(entry.code);
        return message ? [{ code: optionalString(entry.code), message }] : [];
      })
    : [];
  return { valid: payload.valid === true && errors.length === 0, errors };
}

function historyEvent(value: unknown, index: number): WorkflowHistoryEvent | null {
  if (!isRecord(value)) return null;
  return {
    id: optionalString(value.id) ?? `event-${index}`,
    type:
      optionalString(value.type) ?? optionalString(value.event) ?? optionalString(value.action) ?? 'EVENT',
    at: optionalString(value.at) ?? optionalString(value.createdAt) ?? optionalString(value.timestamp) ?? '',
    actor:
      optionalString(value.actorName) ??
      optionalString(value.actor) ??
      optionalString(value.by) ??
      optionalString(value.createdBy),
    detail:
      optionalString(value.detail) ??
      optionalString(value.reason) ??
      optionalString(value.note) ??
      optionalString(value.message),
  };
}

function historyList(payload: unknown): WorkflowHistoryEvent[] {
  if (!Array.isArray(payload)) fail('a workflow history feed');
  return payload
    .map((entry, index) => historyEvent(entry, index))
    .filter((entry): entry is WorkflowHistoryEvent => entry !== null);
}

function decision(value: unknown): WorkflowDecision | null {
  if (!isRecord(value)) return null;
  if (value.decision !== 'APPROVE' && value.decision !== 'REJECT') return null;
  const decidedBy = typeof value.decidedBy === 'string' ? value.decidedBy : '';
  return {
    stepNo: typeof value.stepNo === 'number' ? value.stepNo : 0,
    decidedBy,
    decidedByName: optionalString(value.decidedByName) ?? decidedBy,
    decision: value.decision,
    note: optionalString(value.note),
    at: optionalString(value.at) ?? '',
  };
}

function instance(payload: unknown): WorkflowInstance {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.processKey !== 'string' ||
    !INSTANCE_STATUSES.includes(payload.status as WorkflowInstanceStatus)
  ) {
    fail('a workflow instance');
  }
  const snapshot = isRecord(payload.definitionSnapshot) ? payload.definitionSnapshot : {};
  const initiatedBy = typeof payload.initiatedBy === 'string' ? payload.initiatedBy : '';
  return {
    id: payload.id,
    processKey: payload.processKey,
    subjectType: typeof payload.subjectType === 'string' ? payload.subjectType : '',
    subjectId: typeof payload.subjectId === 'string' ? payload.subjectId : '',
    subjectLabel:
      optionalString(payload.subjectLabel) ?? (typeof payload.subjectId === 'string' ? payload.subjectId : ''),
    subjectPayload: isRecord(payload.subjectPayload) ? payload.subjectPayload : {},
    initiatedBy,
    initiatedByName: optionalString(payload.initiatedByName) ?? initiatedBy,
    currentStep:
      typeof payload.currentStep === 'number' && payload.currentStep >= 1
        ? Math.floor(payload.currentStep)
        : 1,
    status: payload.status as WorkflowInstanceStatus,
    decisions: Array.isArray(payload.decisions)
      ? payload.decisions.map(decision).filter((entry): entry is WorkflowDecision => entry !== null)
      : [],
    definitionSnapshot: { steps: steps(snapshot.steps), rules: rules(snapshot.rules) },
    createdAt: optionalString(payload.createdAt) ?? '',
    resolvedAt: optionalString(payload.resolvedAt),
  };
}

function instanceList(payload: unknown): WorkflowInstance[] {
  if (!Array.isArray(payload)) fail('a workflow instance list');
  return payload.flatMap((entry) => {
    try {
      return [instance(entry)];
    } catch {
      return [];
    }
  });
}

function post(payload: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(payload) };
}

/** What a draft carries to the server; the id-bearing fields stay server-side. */
export interface WorkflowDraftPayload {
  title: string;
  description: string;
  steps: WorkflowStep[];
  rules: WorkflowRules;
}

export const workflowsApi = {
  admin: {
    /** The catalogue: every governable process and the law currently over it. */
    processes: (institutionId: string) =>
      core<unknown>(`${adminBase(institutionId)}/processes`).then(processList),

    /** Every version this institution holds for one process, newest lifecycle first. */
    versions: (institutionId: string, processKey: string) =>
      core<unknown>(`${adminBase(institutionId)}/${seg(processKey)}/versions`).then(definitionList),

    createDraft: (institutionId: string, processKey: string, payload: WorkflowDraftPayload) =>
      core<unknown>(`${adminBase(institutionId)}/definitions`, post({ processKey, ...payload })).then(
        definition,
      ),

    /** DRAFT only; the server refuses edits to anything already in force. */
    updateDraft: (institutionId: string, definitionId: string, payload: Partial<WorkflowDraftPayload>) =>
      core<unknown>(`${adminBase(institutionId)}/definitions/${seg(definitionId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).then(definition),

    validate: (institutionId: string, definitionId: string) =>
      core<unknown>(`${adminBase(institutionId)}/definitions/${seg(definitionId)}/validate`, post({})).then(
        validationReport,
      ),

    /** The only way a definition changes status. The server owns the machine;
     *  this client only ever offers the moves that machine permits. */
    transition: (
      institutionId: string,
      definitionId: string,
      toStatus: WorkflowDefinitionStatus,
      reason?: string,
    ) =>
      core<unknown>(
        `${adminBase(institutionId)}/definitions/${seg(definitionId)}/transition`,
        post(reason ? { toStatus, reason } : { toStatus }),
      ).then(definition),

    history: (institutionId: string, definitionId: string) =>
      core<unknown>(`${adminBase(institutionId)}/definitions/${seg(definitionId)}/history`).then(
        historyList,
      ),
  },

  member: {
    /** The cases waiting on the CALLER — the server resolves who that is. */
    inbox: (institutionId: string) =>
      core<unknown>(`${memberBase(institutionId)}/inbox`).then(instanceList),

    instances: (institutionId: string, filters: { status?: string; processKey?: string } = {}) => {
      const query = new URLSearchParams();
      if (filters.status) query.set('status', filters.status);
      if (filters.processKey) query.set('processKey', filters.processKey);
      const qs = query.toString();
      return core<unknown>(`${memberBase(institutionId)}/instances${qs ? `?${qs}` : ''}`).then(
        instanceList,
      );
    },

    instance: (institutionId: string, instanceId: string) =>
      core<unknown>(`${memberBase(institutionId)}/instances/${seg(instanceId)}`).then(instance),

    decide: (institutionId: string, instanceId: string, verdict: 'APPROVE' | 'REJECT', note?: string) =>
      core<unknown>(
        `${memberBase(institutionId)}/instances/${seg(instanceId)}/decide`,
        post(note ? { decision: verdict, note } : { decision: verdict }),
      ).then(instance),

    cancel: (institutionId: string, instanceId: string) =>
      core<unknown>(`${memberBase(institutionId)}/instances/${seg(instanceId)}/cancel`, post({})).then(
        instance,
      ),
  },
};
